import {
  PREFLIGHT_KEYS,
  PREFLIGHT_FIELD,
  STATIONS,
  type MobilisationEvent,
  type FlagClearedPayload,
  type KitIssuedPayload,
} from '@hcc/types';
import { projectTasksClientSide } from '../../../lib/projectTasks';
import type { ActiveCheck, CheckStatus, Preflight } from './types';

/**
 * Pure derivations behind the person page summary strip. Everything
 * computes from data the page already fetches (metadata + checks +
 * the event chain); no extra network calls.
 */

export interface PreflightSummary {
  done: number;
  total: number;
  anyMissing: boolean;
  anyPending: boolean;
}

/**
 * Count the four pre-employment gates. A gate counts as done when its
 * status is ok or n/a, or when it's locked by an approved onboarding
 * task. A gate owned by an in-flight screening check counts as
 * pending regardless of the stored status.
 */
export function preflightSummary(
  metadata: Record<string, unknown>,
  checks: ActiveCheck[],
): PreflightSummary {
  const preflight = (metadata.preflight ?? {}) as Partial<Preflight>;
  let done = 0;
  let anyMissing = false;
  let anyPending = false;
  for (const key of PREFLIGHT_KEYS) {
    if (preflight.sources?.[key]) {
      done += 1;
      continue;
    }
    if (checks.some((c) => c.preflight_key === key)) {
      anyPending = true;
      continue;
    }
    const status: CheckStatus = preflight[PREFLIGHT_FIELD[key]] ?? 'pending';
    if (status === 'ok' || status === 'na') done += 1;
    else if (status === 'missing') anyMissing = true;
    else anyPending = true;
  }
  return { done, total: PREFLIGHT_KEYS.length, anyMissing, anyPending };
}

/** Plain to-do tasks (not onboarding-kind) that are not complete yet. */
export function outstandingTaskCount(events: MobilisationEvent[] | null): number {
  if (!events) return 0;
  return projectTasksClientSide(events).filter(
    (t) => !t.onboarding_kind && t.status !== 'complete',
  ).length;
}

export interface KitReceiptSummary {
  done: number;
  total: number;
}

/** Distinct stations with a signed KitIssued event, over all stations. */
export function kitReceiptSummary(events: MobilisationEvent[] | null): KitReceiptSummary {
  const issued = new Set<string>();
  for (const ev of events ?? []) {
    if (ev.event_type !== 'KitIssued') continue;
    const p = ev.payload as KitIssuedPayload;
    if (p.station) issued.add(p.station);
  }
  return { done: issued.size, total: STATIONS.length };
}

/** FlagRaised events not yet referenced by a FlagCleared. */
export function activeFlagCount(events: MobilisationEvent[] | null): number {
  if (!events) return 0;
  const cleared = new Set<string>();
  for (const ev of events) {
    if (ev.event_type !== 'FlagCleared') continue;
    const p = ev.payload as FlagClearedPayload;
    if (p.flag_event_id) cleared.add(p.flag_event_id);
  }
  return events.filter((ev) => ev.event_type === 'FlagRaised' && !cleared.has(ev.id))
    .length;
}

/** Latest PersonArchived / PersonUnarchived wins. */
export function isArchived(events: MobilisationEvent[] | null): boolean {
  if (!events) return false;
  const toggles = events
    .filter(
      (e) => e.event_type === 'PersonArchived' || e.event_type === 'PersonUnarchived',
    )
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  return toggles[0]?.event_type === 'PersonArchived';
}
